import { supabase } from '../lib/supabase';

export interface PerformanceAnalysisRow {
  pa_id: string;
  pa_performance_id: string;
  pa_pitch_accuracy: number;
  pa_timing_accuracy: number;
  pa_note_transition_score: number;
  pa_overall_score: number;
  pa_detailed_feedback: any;
  pa_created_at: string;
  pa_status_code: number;
  pa_error_message: string;
  pa_coaching_response: string;
  pa_report_compressed_b64: string;

  p_id: string;
  p_user_id: string;
  p_song_id: string;
  p_vocal_recording_url: string;
  p_duration_seconds: number;
  p_score: number;
  p_vocal_score_data: any;
  p_created_at: string;
  p_updated_at: string;
  p_video_recording_url: string;
  p_video_thumbnail_url: string;
  p_video_duration_seconds: number;
  p_recording_id: string;

  r_id: string;
  r_user_id: string;
  r_song_id: string;
  r_recording_url: string;
  r_recording_type: string;
  r_duration: number;
  r_created_at: string;
  r_updated_at: string;
  r_mixed_recording_url: string;
  r_title: string;
  r_shared_url: string;
  r_shared: boolean;
  r_shared_at: string;
  r_vocals_only_url: string;
  r_upload_status: string;
  r_local_uri: string;
  r_processing_command_id: string;
  r_processing_status: string;
  r_processing_error: string;
  r_video_processing_status: string;
  r_video_processing_error: string;
  r_final_video_url: string;
  r_vocals_preprocessed_url: string;
  r_preprocessing_status: string;
  r_preprocessing_error: string;
  r_audio_sync_offset: number;

  s_id: string;
  s_title: string;
  s_artist: string;
  s_genre: string;
  s_release_year: number;
  s_difficulty: string;
  s_duration: number;
  s_audio_url: string;
  s_cover_url: string;
  s_lead_vocal_url: string;
  s_distribution_eligible: boolean;
  s_created_at: string;
  s_updated_at: string;
  s_catalog_number: string;
  s_slug: string;
  s_lyrics_url: string;
  s_midi_url: string;
  s_full_mix_url: string;
  s_likes_count: number;
  s_is_deleted: boolean;
  s_deleted_at: string;
  s_deleted_by: string;

  u_id: string;
  u_email: string;
  u_username: string;
  u_full_name: string;
  u_avatar_url: string;
  u_bio: string;
  u_created_at: string;
  u_updated_at: string;
  u_role: string;
  u_onboarding_complete: boolean;

  total_count: number;
}

export interface DebugFetchParams {
  filterUserId?: string;
  filterRecordingId?: string;
  filterPerformanceId?: string;
  startDate: string;
  endDate: string;
  pageNum?: number;
  pageSize?: number;
}

export class DebugService {
  static async fetchDebugData(params: DebugFetchParams): Promise<{ data: PerformanceAnalysisRow[]; totalCount: number }> {
    const { data: result, error } = await supabase.rpc(
      'get_performance_analysis_debug',
      {
        filter_user_id: params.filterUserId || null,
        filter_start_date: params.startDate ? `${params.startDate}T00:00:00Z` : null,
        filter_end_date: params.endDate ? `${params.endDate}T23:59:59Z` : null,
        page_num: params.pageNum || 1,
        page_size: params.pageSize || 50,
        filter_recording_id: params.filterRecordingId || null,
        filter_performance_id: params.filterPerformanceId || null
      }
    );

    if (error) throw error;

    const rows = (result || []) as PerformanceAnalysisRow[];
    const totalCount = rows.length > 0 ? rows[0].total_count : 0;
    return { data: rows, totalCount };
  }

  static async retryVocalProcessing(recordingId: string, recordingUrl: string): Promise<void> {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-video-audio`;
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ recording_id: recordingId, recording_url: recordingUrl })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to trigger processing');
    }
  }

  static async retryVideoProcessing(recordingId: string, recordingUrl: string, vocalsOnlyUrl: string, backingTrackUrl?: string): Promise<void> {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-video-merge`;
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        recording_id: recordingId,
        recording_url: recordingUrl,
        vocals_only_url: vocalsOnlyUrl,
        backing_track_url: backingTrackUrl
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to trigger video processing');
    }
  }

  static async deletePerformance(performanceAnalysisId: string, performanceId: string): Promise<void> {
    const { error: paError } = await supabase
      .from('performance_analysis')
      .delete()
      .eq('id', performanceAnalysisId);

    if (paError) throw paError;

    const { error: perfError } = await supabase
      .from('performances')
      .delete()
      .eq('id', performanceId);

    if (perfError) throw perfError;
  }

  static async rescorePerformance(row: PerformanceAnalysisRow): Promise<void> {
    const { error: paError } = await supabase
      .from('performance_analysis')
      .delete()
      .eq('id', row.pa_id);

    if (paError) throw paError;

    const { error: perfError } = await supabase
      .from('performances')
      .delete()
      .eq('id', row.p_id);

    if (perfError) throw perfError;

    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-score`;
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: row.p_user_id,
        song_id: row.p_song_id,
        VoiceReference: row.s_lead_vocal_url,
        VoiceRecording: row.r_vocals_only_url,
        recording_id: row.r_id
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to trigger rescoring');
    }
  }

  static async triggerPreprocessing(recordingId: string): Promise<void> {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/preprocess-audio`;
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ recording_id: recordingId })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to trigger preprocessing');
    }
  }

  static async fetchRecordingsForBulkSync(): Promise<any[]> {
    const { data: recordings, error } = await supabase
      .from('recordings')
      .select('*, song:songs!inner(*)')
      .eq('recording_type', 'video')
      .is('audio_sync_offset', null);

    if (error) throw error;
    return recordings || [];
  }

  static async fetchRecordingById(recordingId: string): Promise<any> {
    const { data, error } = await supabase
      .from('recordings')
      .select('*, song:songs(*)')
      .eq('id', recordingId)
      .single();

    if (error) throw error;
    return data;
  }
}
